import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { IApiQueryOptions } from 'api/ApiClient';
import { ButtonExportCSVAsync, DateRangePicker, FieldWrapper, Filter, FilterButton, Input, PaginatedList, PaidPaymentsTable, Section, SectionHeader, Select } from 'components';
import { DDOption } from 'config/const';
import { lineHeight, theme, toRem } from 'design';
import { useDebounce } from 'hooks';
import { getQueryStringValue, useQueryString } from 'hooks/useQueryString';
import { BusinessDayLabel } from 'screens/Payments/styles';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentDateRange } from 'store/App';
import { getSitePayoutsRequest, Payouts, selectPayouts, selectTotalCountPayouts } from 'store/Collections/Payouts';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { getSitePayoutStatsRequest, selectPayoutStatsByDate, selectPayoutStatsNetTotal, selectPayoutStatsStatusCount, Stats } from 'store/Stats';
import { IStore } from 'store/types';
import { ApiQueryOperator, DisbursementMethod, IApiQueryParam, PAYOUT_STATUSES_COUNTED_IN_TOTALS, PayoutStatus } from 'types';
import { DEFAULT_OFFSET, DEFAULT_PAGE_SIZE } from 'utils';
import { CSVFormatter, IExportCSV } from 'utils/csvformatter';
import { centsToDollarsDisplay } from 'utils/currency';
import { capitalize } from 'utils/strings';
import * as API from '../../api';
import { PaidPaymentsChart, ReportTableHeader } from './components';

const ReportSectionHeader = styled(SectionHeader)`
  h3 {
    font-size: ${toRem(theme.font.size['3xl'])};
    line-height: ${lineHeight(theme.font.size['3xl'])};
  }

  .subtitle {
    margin-left: ${toRem(theme.space.sm)};
    font-size: ${toRem(theme.font.size.md)};
    line-height: ${lineHeight(theme.font.size.md)};
  }
`;

const DEFAULT_SORT = 'employee_name';

const ReportsPaidPayments = () => {
  const dispatch = useDispatch();
  const currentDateRange = useSelector(selectCurrentDateRange());
  const [filterActive, setFilterActive] = useState<boolean>(false);
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payouts.GET_SITE_PAYOUTS_REQUEST));

  const currentSite = useSelector(selectCurrentEmployeeSite);

  const payouts = useSelector(selectPayouts());
  const payoutsTotal = useSelector(selectTotalCountPayouts());

  const paymentStats = useSelector(selectPayoutStatsByDate());
  const isStatsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Stats.GET_SITE_PAYOUT_STATS_REQUEST));

  const paidTotal = useSelector(selectPayoutStatsNetTotal(PayoutStatus.PAID)) || 0;
  const payrollTotal = useSelector(selectPayoutStatsNetTotal(PayoutStatus.PAYROLL)) || 0;
  const cashTotal = useSelector(selectPayoutStatsNetTotal(PayoutStatus.CASH)) || 0;
  const paidCount = useSelector(selectPayoutStatsStatusCount(PayoutStatus.PAID)) || 0;
  const payrollCount = useSelector(selectPayoutStatsStatusCount(PayoutStatus.PAYROLL)) || 0;
  const cashCount = useSelector(selectPayoutStatsStatusCount(PayoutStatus.CASH)) || 0;

  const totalPaidPayments = paidTotal + payrollTotal + cashTotal;
  const totalPaidCount = paidCount + payrollCount + cashCount;

  const [urlQuery, setUrlQuery]: [IApiQueryOptions, React.Dispatch<React.SetStateAction<IApiQueryOptions>>] = useQueryString('reportsPaidPayments', window.location, {
    limit: DEFAULT_PAGE_SIZE,
    offset: DEFAULT_OFFSET,
    sort: DEFAULT_SORT,
    q: [
      { key: 'payment_date', operator: ApiQueryOperator.gte, value: currentDateRange.startDate },
      { key: 'payment_date', operator: ApiQueryOperator.lte, value: currentDateRange.endDate },
    ],
  });
  const startDateRange = getQueryStringValue(urlQuery.q, 'payment_date', ApiQueryOperator.gte) as string;
  const endDateRange = getQueryStringValue(urlQuery.q, 'payment_date', ApiQueryOperator.lte) as string;
  const nameTextSearch = getQueryStringValue(urlQuery.q, 'employee_name', ApiQueryOperator.like) as string;
  const paymentIdTextSearch = getQueryStringValue(urlQuery.q, 'id', ApiQueryOperator.like) as string;
  const disbursementMethodFilters = getQueryStringValue(urlQuery.q, 'disbursement_method', ApiQueryOperator.in) as string;
  const disbursementMethodValues = disbursementMethodFilters?.split(',').map((method: string) => ({ label: capitalize(method), value: method }) as DDOption) ?? [];
  const activeFilterCount = useMemo(() => (urlQuery.q ?? []).filter((item: IApiQueryParam) => !['payment_date', 'status'].includes(item.key)).length, [urlQuery.q]);

  const fetchData = () => {
    if (currentSite?.id && startDateRange && endDateRange) {
      const filters = [...(urlQuery.q || []), { key: 'status', operator: ApiQueryOperator.in, value: PAYOUT_STATUSES_COUNTED_IN_TOTALS }];
      const filtersWithoutPaymentDate = filters.filter((q) => q.key !== 'payment_date');

      dispatch(getSitePayoutStatsRequest(currentSite.id, startDateRange, endDateRange, { q: filtersWithoutPaymentDate }));
      dispatch(getSitePayoutsRequest(currentSite.id, { ...urlQuery, q: filters }));
    }
  };

  const exportCSV = async (siteId: string, q?: IApiQueryParam[], sort?: string): Promise<IExportCSV> => {
    const response = await API.DataExport.sitePayouts(siteId, { q: [...q, { key: 'status', operator: ApiQueryOperator.in, value: PAYOUT_STATUSES_COUNTED_IN_TOTALS }], sort, offset: 0, limit: 0 });
    return CSVFormatter.payouts(response.body);
  };

  const onDateRangeChange = ([start, end]: string[]) => {
    const otherFilters = (urlQuery.q || []).filter((query: any) => query.key !== 'payment_date') as IApiQueryParam[];
    const isValid = dayjs(start, 'YYYY-MM-DD', true).isValid() && dayjs(end, 'YYYY-MM-DD', true).isValid();
    if (isValid) {
      const newFilters = [...otherFilters, { key: 'payment_date', operator: ApiQueryOperator.gte, value: start }, { key: 'payment_date', operator: ApiQueryOperator.lte, value: end }];
      setUrlQuery({ q: newFilters, offset: 0 });
    }
  };

  const onStringFilterChange = useCallback(
    (newValue: string, key: 'employee_name' | 'id') => {
      const otherFilters = (urlQuery.q || []).filter((query: any) => query.key !== key) as IApiQueryParam[];
      const newFilters = newValue.trim() !== '' ? [...otherFilters, { key, operator: ApiQueryOperator.like, value: newValue }] : otherFilters;
      setUrlQuery({ q: newFilters, offset: 0 });
    },
    [setUrlQuery, urlQuery.q]
  );

  const onDisbursementChange = useCallback(
    (newValues: DDOption[]) => {
      const otherFilters = (urlQuery.q || []).filter((query: any) => query.key !== 'disbursement_method') as IApiQueryParam[];
      const newFilters = newValues.length > 0 ? [...otherFilters, { key: 'disbursement_method', operator: ApiQueryOperator.in, value: newValues.map((option: DDOption) => option.value).join(',') }] : otherFilters;
      setUrlQuery({ q: newFilters, offset: 0 });
    },
    [setUrlQuery, urlQuery.q]
  );

  const debouncedOnStringFilterChange = useDebounce(onStringFilterChange, 500);
  const debouncedOnDisbursementChange = useDebounce(onDisbursementChange, 500);

  useEffect(() => fetchData(), [dispatch, urlQuery, currentSite?.id]);

  return currentSite ? (
    <Section data-cy="table">
      <ReportSectionHeader title={centsToDollarsDisplay(totalPaidPayments)} value={`(${totalPaidCount} payments)`} isLoading={isStatsLoading}>
        <BusinessDayLabel>{isLoading ? <Skeleton width={90} /> : 'Business Days'}</BusinessDayLabel>
        <DateRangePicker fluidWidth={false} inputHeight="small" onSubmit={onDateRangeChange} pickerPosition="right" startDate={currentDateRange.startDate} endDate={currentDateRange.endDate} submitLabel="apply" />
      </ReportSectionHeader>

      <PaidPaymentsChart paymentStats={paymentStats} isLoading={isStatsLoading} />

      <ReportTableHeader
        title="Results"
        count={payoutsTotal || 0}
        actions={
          <>
            <FilterButton filterActive={filterActive} appliedFilters={activeFilterCount} onClick={() => setFilterActive(!filterActive)} />
            <ButtonExportCSVAsync disabled={!payoutsTotal} exportFunc={() => exportCSV(currentSite.id, urlQuery.q, urlQuery.sort)} fileName={`PaidPayments_${currentSite.name}_${startDateRange}_${endDateRange}`} />
          </>
        }
        isLoading={isLoading}
      />

      <Filter isVisible={filterActive}>
        <FieldWrapper label="Name" data-cy="filter-name">
          <Input placeholder="Name" defaultValue={nameTextSearch} onChange={(e: React.FormEvent<HTMLInputElement>) => debouncedOnStringFilterChange(e.currentTarget.value, 'employee_name')} />
        </FieldWrapper>
        <FieldWrapper label="Payment ID" data-cy="filter-payment">
          <Input placeholder="Payment ID" defaultValue={paymentIdTextSearch} onChange={(e: React.FormEvent<HTMLInputElement>) => debouncedOnStringFilterChange(e.currentTarget.value, 'id')} />
        </FieldWrapper>
        <FieldWrapper label="Disbursement">
          <Select
            options={[
              {
                label: capitalize(DisbursementMethod.INSTANT),
                value: DisbursementMethod.INSTANT,
              },
              {
                label: capitalize(DisbursementMethod.PAYROLL),
                value: DisbursementMethod.PAYROLL,
              },
              {
                label: capitalize(DisbursementMethod.CASH),
                value: DisbursementMethod.CASH,
              },
            ]}
            values={disbursementMethodValues}
            multi
            onChange={(selected) => debouncedOnDisbursementChange(selected)}
            data-cy="disbursement-method-select"
          />
        </FieldWrapper>
      </Filter>

      <PaginatedList total={payoutsTotal} limit={urlQuery.limit} offset={urlQuery.offset} onPageChange={(options: IApiQueryOptions) => setUrlQuery(options)}>
        <PaidPaymentsTable isLoading={isLoading} payouts={payouts} urlQuery={urlQuery} onSortChange={(newSort: string) => setUrlQuery({ sort: urlQuery.sort === newSort ? `-${urlQuery.sort}` : newSort, offset: 0 })} data-cy="table" />
      </PaginatedList>
    </Section>
  ) : null;
};

export default ReportsPaidPayments;
